import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { MapPin, Phone, Mail, Clock, Send, MessageCircle } from 'lucide-react';
import { useState } from 'react';
import { useSEO } from '../../utils/seo';

export default function Contact() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  useSEO({
    title: 'İletişim - IQCERT Belgelendirme ve Denetim | Bize Ulaşın',
    description: 'IQCERT ile iletişime geçin. İstanbul, Ankara, İzmir ve Bursa ofislerimizden bize ulaşabilir, belgelendirme hizmetleri hakkında bilgi alabilirsiniz. Tel: +90 212 345 67 89',
    keywords: 'IQCERT iletişim, belgelendirme teklif, ISO danışmanlık, İstanbul ofis, iletişim formu',
    canonical: `${siteUrl}/contact`,
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'ContactPage',
          '@id': `${siteUrl}/contact#contactpage`,
          url: `${siteUrl}/contact`,
          name: 'İletişim - IQCERT',
          description: 'IQCERT ile iletişime geçin, sorularınızı sorun'
        },
        {
          '@type': 'FAQPage',
          '@id': `${siteUrl}/contact#faqpage`,
          mainEntity: [
            {
              '@type': 'Question',
              name: 'Belgelendirme süreci ne kadar sürer?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Belgelendirme süreci, işletmenizin büyüklüğü ve seçilen standarda göre değişiklik gösterir. Genellikle 2-6 hafta arasında tamamlanır.'
              }
            },
            {
              '@type': 'Question',
              name: 'Hangi sektörlere hizmet veriyorsunuz?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Turizm, imalat, hizmet, enerji, inşaat ve daha birçok sektörde belgelendirme ve denetim hizmetleri sunuyoruz.'
              }
            },
            {
              '@type': 'Question',
              name: 'Sertifika geçerlilik süresi nedir?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'ISO sertifikaları genellikle 3 yıl geçerlidir. Bu süre boyunca yıllık gözetim denetimleri yapılır.'
              }
            }
          ]
        }
      ]
    }
  });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: ''
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form gönderildi:', formData);
    // Form submission logic here
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: 'Adres',
      details: [
        'IQCERT Belgelendirme ve Denetim A.Ş.',
        'Maslak Mahallesi, Büyükdere Caddesi',
        'No: 123, Kat: 15, Sarıyer/İstanbul',
        '34485 Türkiye'
      ]
    },
    {
      icon: Phone,
      title: 'Telefon',
      details: [
        '+90 216 508 24 24',
        '+90 212 345 67 90',
        'Fax: +90 212 345 67 91'
      ]
    },
    {
      icon: Mail,
      title: 'E-posta',
      details: [
        'info@iqcert.com.tr',
        'satis@iqcert.com.tr',
        'destek@iqcert.com.tr'
      ]
    },
    {
      icon: Clock,
      title: 'Çalışma Saatleri',
      details: [
        'Pazartesi - Cuma: 09:00 - 18:00',
        'Cumartesi: 09:00 - 13:00',
        'Pazar: Kapalı'
      ]
    }
  ];

  const services = [
    'Belgelendirme Hizmetleri',
    'Yönetim Sistemleri Belgelendirme',
    'Teknik Kontroller',
    'Denetim ve Gözetim Hizmetleri',
    'Enerji Verimliliği',
    'Özel Denetimler',
    'Danışmanlık Hizmetleri'
  ];

  const offices = [
    {
      city: 'İstanbul (Merkez)',
      address: 'Maslak Mahallesi, Büyükdere Caddesi No: 123',
      phone: '+90 212 345 67 89',
      email: 'istanbul@iqcert.com.tr'
    },
    {
      city: 'Ankara',
      address: 'Çankaya Mahallesi, Atatürk Bulvarı No: 456',
      phone: '+90 312 345 67 89',
      email: 'ankara@iqcert.com.tr'
    },
    {
      city: 'İzmir',
      address: 'Alsancak Mahallesi, Kordon Caddesi No: 789',
      phone: '+90 232 345 67 89',
      email: 'izmir@iqcert.com.tr'
    },
    {
      city: 'Bursa',
      address: 'Osmangazi Mahallesi, Atatürk Caddesi No: 321',
      phone: '+90 224 345 67 89',
      email: 'bursa@iqcert.com.tr'
    }
  ];

  return (
    <div className="min-h-screen">
      <Header />
      <main>
        {/* Hero Section */}
        <section className="relative bg-gradient-to-r from-red-600 to-red-800 text-white py-20">
          <div className="absolute inset-0 bg-black/20"></div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-5xl font-bold mb-6">İletişim</h1>
              <p className="text-xl leading-relaxed">
                IQCERT ekibi olarak sorularınızı yanıtlamak ve size en uygun çözümleri 
                sunmak için buradayız. Bizimle iletişime geçin, uzman ekibimiz size yardımcı olsun.
              </p>
            </div>
          </div>
        </section>

        {/* Contact Form & Info */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12">
              {/* Contact Form */}
              <div className="bg-white rounded-lg shadow-lg p-8">
                <h2 className="text-3xl font-bold text-gray-800 mb-6">Bize Ulaşın</h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">Ad Soyad *</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                        placeholder="Adınız ve soyadınız"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">E-posta *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                        placeholder="E-posta adresiniz"
                      />
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">Telefon</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                        placeholder="Telefon numaranız"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">Şirket</label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                        placeholder="Şirket adınız"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-gray-700 font-medium mb-2">İlgilendiğiniz Hizmet</label>
                    <select
                      name="service"
                      value={formData.service}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    >
                      <option value="">Hizmet seçiniz</option>
                      {services.map((service, index) => (
                        <option key={index} value={service}>{service}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Mesajınız *</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows={5}
                      maxLength={500}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                      placeholder="Mesajınızı buraya yazın... (Maksimum 500 karakter)"
                    ></textarea>
                    <div className="text-right text-sm text-gray-500 mt-1">
                      {formData.message.length}/500
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="w-full bg-red-600 text-white py-3 px-6 rounded-lg hover:bg-red-700 transition-colors font-medium flex items-center justify-center whitespace-nowrap"
                  >
                    <Send className="w-5 h-5 mr-2" />
                    Mesaj Gönder
                  </button>
                </form>
              </div>

              {/* Contact Information */}
              <div className="space-y-8">
                <div>
                  <h2 className="text-3xl font-bold text-gray-800 mb-6">İletişim Bilgileri</h2>
                  <div className="space-y-6">
                    {contactInfo.map((info, index) => (
                      <div key={index} className="flex items-start">
                        <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                          <info.icon className="w-6 h-6 text-red-600" />
                        </div>
                        <div>
                          <h3 className="font-bold text-gray-800 mb-2">{info.title}</h3>
                          {info.details.map((detail, detailIndex) => (
                            <p key={detailIndex} className="text-gray-600">{detail}</p>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Quick Contact */}
                <div className="bg-red-50 p-6 rounded-lg">
                  <h3 className="text-xl font-bold text-gray-800 mb-4">Hızlı İletişim</h3>
                  <div className="space-y-3">
                    <a 
                      href="tel:+902165082424" 
                      className="flex items-center text-red-600 hover:text-red-700 transition-colors"
                    >
                      <Phone className="w-5 h-5 mr-3" />
                      <span className="font-medium">Hemen Ara: +90 216 508 24 24</span>
                    </a>
                    <a 
                      href="mailto:info@iqcert.com.tr" 
                      className="flex items-center text-red-600 hover:text-red-700 transition-colors"
                    >
                      <Mail className="w-5 h-5 mr-3" />
                      <span className="font-medium">E-posta Gönder</span>
                    </a>
                    <button className="flex items-center text-red-600 hover:text-red-700 transition-colors">
                      <MessageCircle className="w-5 h-5 mr-3" />
                      <span className="font-medium">WhatsApp ile İletişim</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Map Section */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-800 mb-4">Ofislerimiz</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Türkiye'nin dört bir yanında ofislerimizle size daha yakınız
              </p>
            </div>
            
            {/* Google Map */}
            <div className="mb-12">
              <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3008.8234567890123!2d29.0123456789!3d41.0123456789!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDHCsDAwJzQ0LjQiTiAyOcKwMDAnNDQuNCJF!5e0!3m2!1str!2str!4v1234567890123!5m2!1str!2str"
                  width="100%"
                  height="400"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="IQCERT Ofis Konumu"
                ></iframe>
              </div>
            </div>

            {/* Office Locations */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {offices.map((office, index) => (
                <div key={index} className="bg-white p-6 rounded-lg shadow-lg">
                  <h3 className="text-xl font-bold text-gray-800 mb-3">{office.city}</h3>
                  <div className="space-y-2 text-gray-600">
                    <p className="flex items-start">
                      <MapPin className="w-4 h-4 mr-2 mt-1 flex-shrink-0 text-red-600" />
                      {office.address}
                    </p>
                    <p className="flex items-center">
                      <Phone className="w-4 h-4 mr-2 text-red-600" />
                      {office.phone}
                    </p>
                    <p className="flex items-center">
                      <Mail className="w-4 h-4 mr-2 text-red-600" />
                      {office.email}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-800 mb-4">Sık Sorulan Sorular</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                En çok merak edilen sorular ve cevapları
              </p>
            </div>
            <div className="max-w-3xl mx-auto space-y-6">
              <div className="bg-white p-6 rounded-lg shadow-lg">
                <h3 className="text-lg font-bold text-gray-800 mb-2">Belgelendirme süreci ne kadar sürer?</h3>
                <p className="text-gray-600">Belgelendirme süreci, işletmenizin büyüklüğü ve seçilen standarda göre değişiklik gösterir. Genellikle 2-6 hafta arasında tamamlanır.</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-lg">
                <h3 className="text-lg font-bold text-gray-800 mb-2">Hangi sektörlere hizmet veriyorsunuz?</h3>
                <p className="text-gray-600">Turizm, imalat, hizmet, enerji, inşaat ve daha birçok sektörde belgelendirme ve denetim hizmetleri sunuyoruz.</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-lg">
                <h3 className="text-lg font-bold text-gray-800 mb-2">Sertifika geçerlilik süresi nedir?</h3>
                <p className="text-gray-600">ISO sertifikaları genellikle 3 yıl geçerlidir. Bu süre boyunca yıllık gözetim denetimleri yapılır.</p>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
